import React, { useEffect, useState } from 'react';
import { apiFetchAuthed } from '../../apiHelpers';
import useChatStore from '../../stores/useChatStore';

type Candidate = { id: string; label: string };

/**
 * "Bring someone in": the people who could join this thread, same list the
 * notes use. One click adds them; the conversation reloads as shared, and
 * Flerpie answers everyone in it with the rights they all share.
 */
const FlerpieInvitePicker = ({ conversationId, onClose }: { conversationId: string; onClose: () => void }) => {
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiFetchAuthed(`/ai/conversations/${conversationId}/candidates`)
      .then((rows: Candidate[]) => { if (alive) setCandidates(rows || []); })
      .catch(() => { if (alive) setError('Could not load people.'); });
    return () => { alive = false; };
  }, [conversationId]);

  const add = async (c: Candidate) => {
    setBusy(c.id);
    setError(null);
    try {
      const conversation = await apiFetchAuthed(`/ai/conversations/${conversationId}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: c.id }),
      });
      useChatStore.getState().applyConversation(conversation);
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const shown = (candidates || []).filter((c) => c.label.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div
      role="dialog"
      aria-label="Bring someone into this thread"
      className="absolute right-2 top-11 z-30 w-56 rounded-xl p-2 flex flex-col gap-1.5"
      style={{ background: 'rgba(30,30,30,0.98)', boxShadow: '0 12px 32px rgba(0,0,0,0.45)' }}
    >
      <input
        autoFocus
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
        placeholder="Who joins this thread?"
        aria-label="Who joins this thread"
        className="w-full bg-transparent text-[12px] text-gray-200 placeholder:text-gray-600 rounded-md px-2 py-1.5 m-0"
        style={{ border: '1px solid rgba(255,255,255,0.08)' }}
      />
      <div className="max-h-48 overflow-y-auto flex flex-col">
        {candidates === null && !error && <div className="text-[11px] text-gray-500 px-2 py-1">Loading...</div>}
        {candidates !== null && shown.length === 0 && <div className="text-[11px] text-gray-500 px-2 py-1">Nobody else to add.</div>}
        {shown.map((c) => (
          <button
            key={c.id}
            type="button"
            disabled={busy !== null}
            onClick={() => add(c)}
            className="text-left text-[12px] text-gray-300 hover:text-primary hover:bg-gray-800/60 rounded-md px-2 py-1.5 disabled:opacity-50"
          >
            {busy === c.id ? `Adding ${c.label}...` : c.label}
          </button>
        ))}
      </div>
      {error && <div className="text-[10px] text-red-300 px-2">{error}</div>}
      <div className="text-[10px] text-gray-600 px-2 pb-0.5">They will see this whole conversation, and Flerpie will use only the access you all share.</div>
    </div>
  );
};

export default FlerpieInvitePicker;
